import { useEffect, useMemo, useState, useCallback } from 'react';
import './FacilitiesPage.css';
import { getImageUrl } from './utils/mediaUtils';

const API_BASE = (() => {
  if (process.env.REACT_APP_API_BASE) return process.env.REACT_APP_API_BASE;
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const { hostname, protocol } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://permataharapansecondary.sch.id/api';
    }
    return `${protocol}//${hostname}/api`;
  }
  return 'https://permataharapansecondary.sch.id/api';
})();

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg') || lower.includes('youtube.com') || lower.includes('youtu.be');
};

const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return m ? m[1] : null;
};

function FacilitiesPage({ page, onNavigate }) {
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_homepage');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  const [liveSections, setLiveSections] = useState(() => {
    if (page?.sections && Object.keys(page.sections).length > 0) {
      return page.sections;
    }
    return null;
  });

  // 1. Fetch CMS media map
  useEffect(() => {
    let isMounted = true;
    const loadMedia = async () => {
      try {
        const res = await fetch(`${API_BASE}/homepage/cms/media`, { credentials: 'omit' });
        if (res.ok) {
          const json = await res.json();
          const items = Array.isArray(json.data) ? json.data : [];
          const map = {};
          items.forEach(m => {
            const mId = m.id || m.media_id;
            const mUrl = m.url || m.file_path;
            if (mId && mUrl) {
              map[mId] = mUrl;
              map[String(mId)] = mUrl;
            }
            if (m.filename && mUrl) map[m.filename] = mUrl;
            if (m.original_filename && mUrl) map[m.original_filename] = mUrl;
          });
          if (isMounted && Object.keys(map).length > 0) {
            setMediaMap(map);
            try { sessionStorage.setItem('cms_media_homepage', JSON.stringify(map)); } catch (_) {}
          }
        }
      } catch (e) {
        console.warn('[FacilitiesPage] Media fetch error:', e);
      }
    };
    loadMedia();


  return () => { isMounted = false; };
  }, []);

  // 2. Fetch live published sections directly from database
  useEffect(() => {
    let isMounted = true;
    const fetchPublishedSections = async () => {
      try {
        const res = await fetch(`${API_BASE}/homepage/cms/sections?published=true&page=facilities&_t=${Date.now()}`, {
          credentials: 'omit',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        if (res.ok) {
          const json = await res.json();
          const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
          if (items.length > 0) {
            const secMap = {};
            items.forEach(s => {
              const cp = (s.content_published && s.content_published !== '{}' && s.content_published !== '[]')
                ? s.content_published
                : (s.content || s.content_published);
              let parsed = null;
              try {
                parsed = typeof cp === 'string' ? JSON.parse(cp) : cp;
              } catch (e) {
                parsed = cp;
              }
              if (parsed) {
                secMap[s.section_type] = parsed.en || parsed;
              }
            });
            if (isMounted && Object.keys(secMap).length > 0) {
              setLiveSections(secMap);
            }
          }
        }
      } catch (err) {
        console.warn('[FacilitiesPage] Error fetching live published sections:', err);
      }
    };

    fetchPublishedSections();
    return () => { isMounted = false; };
  }, []);

  // Sync if page prop updates with sections
  useEffect(() => {
    if (page?.sections && Object.keys(page.sections).length > 0) {
      setLiveSections(prev => prev ? { ...page.sections, ...prev } : page.sections);
    }
  }, [page?.sections]);

  // Pure dynamic media resolver - strictly zero hardcoded image fallbacks
  const resolveMedia = useCallback((src) => {
    if (!src) return '';
    if (typeof src === 'object' && src !== null) {
      src = src.image || src.url || src.file_path || src.src || '';
    }
    const str = String(src).trim();
    if (!str) return '';
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (mediaMap && (mediaMap[str] || mediaMap[String(str)])) {
      return mediaMap[str] || mediaMap[String(str)];
    }
    const helperUrl = getImageUrl(str, mediaMap);
    if (helperUrl && helperUrl !== str && helperUrl !== '') {
      return helperUrl;
    }
    if (str.startsWith('/')) {
      const path = str.startsWith('/api/') ? str : `/api${str}`;
      return `${API_ORIGIN}${path}`;
    }
    if (str.includes('uploads/')) {
      return `${API_ORIGIN}/api/${str.replace(/^\/+/, '')}`;
    }
    return `${API_ORIGIN}/api/uploads/images/${str.replace(/^\/+/, '')}`;
  }, [mediaMap]);

  // Section references extracted directly from content_published
  const heroSec = liveSections?.['facilities-hero'] || page?.sections?.['facilities-hero'] || page?.hero || {};
  const overviewSec = liveSections?.['facilities-overview'] || page?.sections?.['facilities-overview'] || {};
  const techSec = liveSections?.['facilities-technology'] || page?.sections?.['facilities-technology'] || {};
  const safetySec = liveSections?.['facilities-safety'] || page?.sections?.['facilities-safety'] || {};
  const gallerySec = liveSections?.['facilities-gallery'] || page?.sections?.['facilities-gallery'] || {};

  const cleanHeroText = (val, fallback = '') => {
    if (!val || val === 'null' || val === 'undefined') return fallback;
    return String(val).trim();
  };

  // 1. HERO SECTION DATA
  const heroKicker = cleanHeroText(heroSec?.kicker || page?.hero_kicker || page?.kicker);
  const heroTitle = cleanHeroText(heroSec?.title || page?.hero_title || page?.title);
  const heroSummary = cleanHeroText(heroSec?.summary || heroSec?.subtitle || page?.hero_summary || page?.summary);

  const rawHeroSlides = useMemo(() => {
    if (Array.isArray(heroSec?.slides) && heroSec.slides.length > 0) return heroSec.slides;
    if (Array.isArray(page?.hero_slides) && page.hero_slides.length > 0) return page.hero_slides;
    if (Array.isArray(page?.slides) && page.slides.length > 0) return page.slides;
    return [];
  }, [heroSec?.slides, page?.hero_slides, page?.slides]);

  const heroSlides = useMemo(() => {
    return rawHeroSlides
      .map((slide, idx) => {
        const rawUrl = typeof slide === 'object' && slide !== null
          ? (slide.image || slide.url || slide.file_path || slide.video || '')
          : (typeof slide === 'string' ? slide : '');
        const resolved = resolveMedia(rawUrl);
        const videoCandidate = (typeof slide === 'object' && slide !== null && slide.video)
          ? resolveMedia(slide.video)
          : (isVideoUrl(resolved) ? resolved : null);
        const isVideo = Boolean(videoCandidate);
        const youtubeId = isVideo ? extractYouTubeId(videoCandidate) : null;
        return {
          id: (typeof slide === 'object' && slide?.order !== undefined) ? slide.order : idx,
          url: videoCandidate || resolved,
          isVideo,
          isYouTube: Boolean(youtubeId),
          youtubeId
        };
      })
      .filter(s => Boolean(s.url));
  }, [rawHeroSlides, resolveMedia]);

  const [activeHero, setActiveHero] = useState(0);
  const [activeGallery, setActiveGallery] = useState(0);

  useEffect(() => {
    if (!heroSlides.length) return;
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.facility-animate');
    if (!animatedItems.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  // 2. CAMPUS OVERVIEW DATA
  const overviewKicker = cleanHeroText(overviewSec?.kicker || page?.overview_kicker);
  const overviewTitle = cleanHeroText(overviewSec?.title || page?.overview_title);
  const overviewDescription = cleanHeroText(overviewSec?.description || page?.overview_description);

  const campusOverview = useMemo(() => {
    const list = Array.isArray(overviewSec?.items)
      ? overviewSec.items
      : (Array.isArray(page?.campus_overview) ? page.campus_overview : []);

    return list.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `facility-${index + 1}`,
          title: item,
          description: '',
          image: '',
        };
      }

      const title = item?.title || '';
      const normalizedId = String(item?.id || title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      return {
        id: normalizedId || `facility-${index + 1}`,
        title,
        description: item?.description || '',
        image: resolveMedia(item?.image),
      };
    });
  }, [overviewSec?.items, page?.campus_overview, resolveMedia]);

  // 3. TECHNOLOGY & INNOVATION DATA
  const techKicker = cleanHeroText(techSec?.kicker || page?.tech_kicker);
  const techTitle = cleanHeroText(techSec?.title || page?.tech_title);
  const techDescription = cleanHeroText(techSec?.description || page?.tech_description);

  const technologyInnovation = useMemo(() => {
    const list = Array.isArray(techSec?.items)
      ? techSec.items
      : (Array.isArray(page?.technology_innovation) ? page.technology_innovation : []);

    return list.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `technology-${index + 1}`,
          title: item,
          badge: 'Innovation',
          description: '',
          image: '',
        };
      }

      const title = item?.title || '';
      const normalizedId = String(item?.id || title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      return {
        id: normalizedId || `technology-${index + 1}`,
        title,
        badge: item?.badge || 'Innovation',
        description: item?.description || '',
        image: resolveMedia(item?.image),
      };
    });
  }, [techSec?.items, page?.technology_innovation, resolveMedia]);

  // 4. SAFETY & SECURITY DATA
  const safetyKicker = cleanHeroText(safetySec?.kicker || page?.safety_kicker);
  const safetyTitle = cleanHeroText(safetySec?.title || page?.safety_title);
  const safetyDescription = cleanHeroText(safetySec?.description || page?.safety_description);

  const safetySecurity = useMemo(() => {
    const list = Array.isArray(safetySec?.items)
      ? safetySec.items
      : (Array.isArray(page?.safety_security) ? page.safety_security : []);

    return list.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `safety-${index + 1}`,
          title: item,
          description: '',
          image: '',
        };
      }

      const title = item?.title || '';
      const normalizedId = String(item?.id || title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      return {
        id: normalizedId || `safety-${index + 1}`,
        title,
        description: item?.description || '',
        image: resolveMedia(item?.image),
      };
    });
  }, [safetySec?.items, page?.safety_security, resolveMedia]);

  // 5. PHOTO GALLERY DATA
  const galleryKicker = cleanHeroText(gallerySec?.kicker || page?.gallery_kicker);
  const galleryTitle = cleanHeroText(gallerySec?.title || page?.gallery_title);
  const galleryDescription = cleanHeroText(gallerySec?.description || page?.gallery_description);

  const photoGallery = useMemo(() => {
    const list = Array.isArray(gallerySec?.items)
      ? gallerySec.items
      : (Array.isArray(page?.photo_gallery) ? page.photo_gallery : []);

    return list.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `gallery-${index + 1}`,
          title: item,
          description: '',
          image: '',
        };
      }

      const title = item?.title || '';
      const normalizedId = String(item?.id || title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      return {
        id: normalizedId || `gallery-${index + 1}`,
        title,
        description: item?.description || '',
        image: resolveMedia(item?.image),
      };
    });
  }, [gallerySec?.items, page?.photo_gallery, resolveMedia]);

  useEffect(() => {
    if (!photoGallery.length) return;
    const intervalId = setInterval(() => {
      setActiveGallery((current) => (current + 1) % photoGallery.length);
    }, 5000);
    return () => clearInterval(intervalId);
  }, [photoGallery.length]);

  // 6. VIRTUAL TOUR DATA
  const virtualTour = useMemo(() => {
    const raw = liveSections?.['facilities-tour'] || liveSections?.['virtual-tour'] || page?.sections?.['facilities-tour'] || page?.sections?.['virtual-tour'] || page?.virtual_tour || {};
    return {
      kicker: cleanHeroText(raw.kicker),
      title: cleanHeroText(raw.title),
      summary: cleanHeroText(raw.summary || raw.description),
      ctaLabel: cleanHeroText(raw.ctaLabel || raw.cta_label),
      ctaHref: raw.ctaHref || raw.cta_href || '#',
      image: resolveMedia(raw.image),
    };
  }, [liveSections, page?.sections, page?.virtual_tour, resolveMedia]);


  const getFacilitySlug = (item, index) => {
    const raw = String(item?.id || item?.title || '').toLowerCase();
    if (raw.includes('classroom') || index === 0) return 'facility-classrooms';
    if (raw.includes('lab') || raw.includes('science') || index === 1) return 'facility-labs';
    if (raw.includes('librar') || raw.includes('common') || index === 2) return 'facility-library';
    if (raw.includes('sport') || raw.includes('athletic') || raw.includes('field') || index === 3) return 'facility-sports-field';
    if (raw.includes('art') || raw.includes('music') || index === 4) return 'facility-art-music-rooms';
    return `facility-${item?.id || 'classrooms'}`;
  };

  const getTechnologySlug = (item, index) => {
    const raw = String(item?.id || item?.title || '').toLowerCase();
    if (raw.includes('smart') || raw.includes('classroom') || index === 0) return 'technology-smart-classrooms';
    if (raw.includes('it') || raw.includes('lab') || raw.includes('coding') || index === 1) return 'technology-it-lab';
    if (raw.includes('maker') || raw.includes('space') || index === 2) return 'technology-maker-space';
    return `technology-${item?.id || 'smart-classrooms'}`;
  };
  return (
    <section className="facilities-page">
      {/* 1. HERO SECTION */}
      <section className="facilities-hero" id="facilities-overview">
        <div className="facilities-hero-media" aria-hidden="true">
          {heroSlides.length > 0 ? (
            heroSlides.map((slide, index) => {
              const isCurrent = index === activeHero;
              return (
                <div
                  key={slide.id || index}
                  className={`facilities-hero-slide ${isCurrent ? 'active' : ''}`}
                >
                  {slide.isVideo ? (
                    slide.isYouTube ? (
                      <iframe
                        className="facilities-hero-video-frame"
                        src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${slide.youtubeId}&controls=0&playsinline=1&modestbranding=1&rel=0`}
                        title="Facilities Hero video background"
                        allow="autoplay; encrypted-media"
                        tabIndex="-1"
                      />
                    ) : (
                      <video
                        className="facilities-hero-video-element"
                        src={slide.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    )
                  ) : (
                    <img
                      src={slide.url}
                      alt={heroTitle || 'Facilities'}
                      className="facilities-hero-img-element"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="facilities-hero-slide active facilities-hero-gradient-fallback" />
          )}
        </div>

        <div className="facilities-hero-overlay" aria-hidden="true" />

        <div className="facilities-hero-content-wrap">
          <div className="facilities-hero-content">
            {heroKicker && (
              <p className="facilities-hero-kicker">
                {heroKicker}
              </p>
            )}

            {heroTitle && (
              <h1 className="facilities-hero-title">
                {heroTitle}
              </h1>
            )}

            {heroSummary && (
              <p className="facilities-hero-summary" style={{ whiteSpace: 'pre-line' }}>
                {heroSummary}
              </p>
            )}

            {heroSlides.length > 1 && (
              <div className="facilities-hero-dots" role="tablist" aria-label="Facilities hero carousel navigation">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.id || index}
                    type="button"
                    className={`facilities-hero-dot ${index === activeHero ? 'active' : ''}`}
                    onClick={() => setActiveHero(index)}
                    aria-label={`Show facility slide ${index + 1}`}
                    role="tab"
                    aria-selected={index === activeHero}
                  >
                    <span className="facilities-hero-dot-bar" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 2. CAMPUS OVERVIEW SECTION */}
      {(campusOverview.length > 0 || overviewTitle) && (
        <section className="facilities-campus" id="campus-overview">
          <div className="facilities-campus-container">
            {/* Header */}
            <div className="facilities-overview-header facility-animate">
              {overviewKicker && (
                <div className="facilities-overview-kicker-badge">
                  <span className="facilities-overview-kicker-dot" />
                  <span>{overviewKicker}</span>
                </div>
              )}
              {overviewTitle && (
                <h2 className="facilities-overview-heading">{overviewTitle}</h2>
              )}
              {overviewDescription && (
                <p className="facilities-overview-desc" style={{ whiteSpace: 'pre-line' }}>{overviewDescription}</p>
              )}
            </div>

            {/* Scalable Multi-Column Grid */}
            {campusOverview.length > 0 && (
              <div className="facilities-overview-grid">
                {campusOverview.map((item, index) => (
                  <article
                    key={item.id}
                    id={item.id}
                    className="facility-overview-card facility-animate"
                    onClick={() => onNavigate && onNavigate(getFacilitySlug(item, index))}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        onNavigate && onNavigate(getFacilitySlug(item, index));
                      }
                    }}
                  >
                    <div className="facility-card-media">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="facility-card-img"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="facility-card-overlay" />
                      <span className="facility-card-number">
                        {index + 1 < 10 ? `0${index + 1}` : index + 1}
                      </span>
                      <span className="facility-card-campus-badge">
                        Permata Harapan
                      </span>
                    </div>

                    <div className="facility-card-body">
                      <div className="facility-card-title-row">
                        <h3 className="facility-card-title">{item.title}</h3>
                      </div>

                      {item.description && (
                        <p className="facility-card-description" style={{ whiteSpace: 'pre-line' }}>{item.description}</p>
                      )}

                      <div className="facility-card-footer">
                        <span className="facility-card-action">
                          <span>Explore Facility</span>
                          <i className="fas fa-arrow-right" aria-hidden="true" />
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 3. TECHNOLOGY & INNOVATION SECTION */}
      {(technologyInnovation.length > 0 || techTitle) && (
        <section className="facilities-technology" id="technology-innovation">
          <div className="facilities-tech-container">
            {/* Header */}
            <div className="facilities-tech-header facility-animate">
              {techKicker && (
                <div className="facilities-tech-kicker-badge">
                  <span className="facilities-tech-pulse-dot" />
                  <span>{techKicker}</span>
                </div>
              )}
              {techTitle && <h3 className="facilities-tech-heading">{techTitle}</h3>}
              {techDescription && <p className="facilities-tech-desc" style={{ whiteSpace: 'pre-line' }}>{techDescription}</p>}
            </div>

            {technologyInnovation.length > 0 && (
              <div className="facilities-tech-grid">
                {technologyInnovation.map((item, index) => (
                  <article
                    key={item.id}
                    id={item.id}
                    className="facility-tech-card facility-animate"
                    onClick={() => onNavigate && onNavigate(getTechnologySlug(item, index))}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        onNavigate && onNavigate(getTechnologySlug(item, index));
                      }
                    }}
                  >
                    <div className="facility-tech-media">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="facility-tech-img"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="facility-tech-overlay" />
                      <span className="facility-tech-num">0{index + 1}</span>
                      {item.badge && <span className="facility-tech-badge">{item.badge}</span>}
                    </div>

                    <div className="facility-tech-body">
                      <h4 className="facility-tech-title">{item.title}</h4>
                      {item.description && <p className="facility-tech-desc-text" style={{ whiteSpace: 'pre-line' }}>{item.description}</p>}

                      <div className="facility-tech-action-wrap">
                        <button
                          type="button"
                          className="facility-tech-explore-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigate && onNavigate(getTechnologySlug(item, index));
                          }}
                        >
                          <span>Explore Technology</span>
                          <i className="fas fa-arrow-right" />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 4. SAFETY & SECURITY SECTION */}
      {(safetySecurity.length > 0 || safetyTitle) && (
        <section className="facilities-safety" id="safety-security">
          <div className="facilities-safety-container">
            {/* Header */}
            <div className="facilities-safety-header facility-animate">
              {safetyKicker && (
                <div className="facilities-safety-kicker-badge">
                  <span className="facilities-safety-kicker-dot" />
                  <span>{safetyKicker}</span>
                </div>
              )}
              {safetyTitle && <h2 className="facilities-safety-heading">{safetyTitle}</h2>}
              {safetyDescription && <p className="facilities-safety-desc" style={{ whiteSpace: 'pre-line' }}>{safetyDescription}</p>}
            </div>

            {safetySecurity.length > 0 && (
              <div className="facilities-safety-4col-grid">
                {safetySecurity.map((item, index) => {
                  const getIcon = (id, title) => {
                    const key = `${id} ${title}`.toLowerCase();
                    if (key.includes('health') || key.includes('protocol') || key.includes('sanit')) return 'fa-heart-pulse';
                    if (key.includes('cctv') || key.includes('monitor') || key.includes('camera')) return 'fa-video';
                    if (key.includes('nurse') || key.includes('medic') || key.includes('first aid')) return 'fa-user-nurse';
                    if (key.includes('emerg') || key.includes('drill') || key.includes('evac')) return 'fa-shield-halved';
                    return 'fa-shield-halved';
                  };
                  const iconName = getIcon(item.id, item.title);

                  return (
                    <article
                      key={item.id}
                      id={item.id}
                      className="facility-safety-pillar-card facility-animate"
                    >
                      <div className="facility-safety-pillar-media">
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="facility-safety-pillar-img"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="facility-safety-pillar-overlay" />
                        <span className="facility-safety-pillar-num">
                          {index + 1 < 10 ? `0${index + 1}` : index + 1}
                        </span>
                      </div>

                      <div className="facility-safety-pillar-badge-wrap">
                        <div className="facility-safety-pillar-icon-circle">
                          <i className={`fas ${iconName}`} aria-hidden="true" />
                        </div>
                      </div>

                      <div className="facility-safety-pillar-body">
                        {item.title && <h3 className="facility-safety-pillar-title">{item.title}</h3>}
                        {item.description && (
                          <p className="facility-safety-pillar-desc" style={{ whiteSpace: 'pre-line' }}>{item.description}</p>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 5. PHOTO GALLERY & VIRTUAL TOUR SECTION */}
      {(photoGallery.length > 0 || galleryTitle || virtualTour.title || virtualTour.image) && (
        <section className="facilities-gallery" id="photo-gallery">
          <div className="facilities-gallery-container">
            {/* Header */}
            {(galleryKicker || galleryTitle || galleryDescription) && (
              <div className="facilities-gallery-header facility-animate">
                {galleryKicker && (
                  <div className="facilities-gallery-kicker-badge">
                    <span className="facilities-gallery-kicker-dot" />
                    <span>{galleryKicker}</span>
                  </div>
                )}
                {galleryTitle && <h2 className="facilities-gallery-heading">{galleryTitle}</h2>}
                {galleryDescription && <p className="facilities-gallery-desc" style={{ whiteSpace: 'pre-line' }}>{galleryDescription}</p>}
              </div>
            )}

            {/* Cinematic Theater Stage */}
            {photoGallery.length > 0 && (() => {
              const currentSlide = photoGallery[activeGallery] || photoGallery[0];
              return (
                <div className="facilities-theater-stage facility-animate">
                  {currentSlide.image && (
                    <img
                      key={currentSlide.id}
                      src={currentSlide.image}
                      alt={currentSlide.title}
                      className="facilities-theater-img"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="facilities-theater-overlay" />

                  {/* Stage Top Controls & Badges */}
                  <div className="facilities-theater-top-bar">
                    <div className="facilities-theater-badge">
                      <span className="facilities-theater-dot" />
                      <span>Campus Moments</span>
                    </div>
                    {photoGallery.length > 1 && (
                      <div className="facilities-theater-counter">
                        {activeGallery + 1 < 10 ? `0${activeGallery + 1}` : activeGallery + 1} / {photoGallery.length < 10 ? `0${photoGallery.length}` : photoGallery.length}
                      </div>
                    )}
                  </div>

                  {/* Prev / Next Arrows */}
                  {photoGallery.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="facilities-theater-arrow prev"
                        onClick={() => setActiveGallery((activeGallery - 1 + photoGallery.length) % photoGallery.length)}
                        aria-label="Previous photo"
                        title="Previous photo"
                      >
                        <i className="fas fa-chevron-left" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="facilities-theater-arrow next"
                        onClick={() => setActiveGallery((activeGallery + 1) % photoGallery.length)}
                        aria-label="Next photo"
                        title="Next photo"
                      >
                        <i className="fas fa-chevron-right" aria-hidden="true" />
                      </button>
                    </>
                  )}

                  {/* Bottom Caption Card */}
                  <div className="facilities-theater-caption">
                    {currentSlide.title && (
                      <h3 className="facilities-theater-title">{currentSlide.title}</h3>
                    )}
                    {currentSlide.description && (
                      <p className="facilities-theater-desc" style={{ whiteSpace: 'pre-line' }}>{currentSlide.description}</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Interactive Thumbnail Selector Strip */}
            {photoGallery.length > 1 && (
              <div className="facilities-gallery-thumbs facility-animate" role="tablist" aria-label="Photo Gallery Thumbnails">
                {photoGallery.map((item, index) => {
                  const isCurrent = index === activeGallery;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={isCurrent}
                      className={`facilities-thumb-card ${isCurrent ? 'is-active' : ''}`}
                      onClick={() => setActiveGallery(index)}
                    >
                      <div className="facilities-thumb-media">
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="facilities-thumb-img"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                      <div className="facilities-thumb-content">
                        <span className="facilities-thumb-index">
                          {index + 1 < 10 ? `0${index + 1}` : index + 1}
                        </span>
                        <span className="facilities-thumb-title">{item.title}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* 6. VIRTUAL TOUR SHOWCASE CARD */}
            {(virtualTour.title || virtualTour.image) && (
              <div className="facilities-tour-card facility-animate" id="virtual-tour">
                {virtualTour.image && (
                  <div className="facilities-tour-media-wrap">
                    <img
                      src={virtualTour.image}
                      alt={virtualTour.title || 'Virtual Tour'}
                      className="facilities-tour-img"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <div className="facilities-tour-media-overlay" />
                    <span className="facilities-tour-badge-360">
                      <i className="fas fa-vr-cardboard" aria-hidden="true" />
                      <span>360 Virtual Tour</span>
                    </span>
                  </div>
                )}

                <div className="facilities-tour-body">
                  {virtualTour.kicker && <p className="facilities-tour-kicker">{virtualTour.kicker}</p>}
                  {virtualTour.title && <h3 className="facilities-tour-title">{virtualTour.title}</h3>}
                  {virtualTour.summary && <p className="facilities-tour-summary" style={{ whiteSpace: 'pre-line' }}>{virtualTour.summary}</p>}
                  {(virtualTour.ctaLabel || virtualTour.ctaHref) && (
                    <a className="facilities-tour-action-btn" href={virtualTour.ctaHref || '#'}>
                      <i className="fas fa-compass" aria-hidden="true" />
                      <span>{virtualTour.ctaLabel || 'Start Virtual Tour'}</span>
                      <i className="fas fa-arrow-right" aria-hidden="true" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </section>
  );
}

export default FacilitiesPage;
